/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { AUTH_LOGIN_BACKGROUND_REGION_ID } from '@/lib/page-designer/auth-login-page';
import { cn } from '@/lib/utils';

/**
 * Fixed to the viewport so PD Image fills the screen even when intermediate
 * Page Designer wrappers do not stretch (see auth background region in Component).
 */
const BACKGROUND_REGION_CLASS =
    'pointer-events-none fixed inset-0 z-0 h-dvh w-full overflow-hidden [&_picture]:block [&_picture]:size-full [&_img]:size-full [&_img]:object-cover';

type AuthPageShellProps = {
    page: Promise<PageWithComponentData | null> | null;
    children: ReactNode;
    className?: string;
};

/**
 * Shared wrapper for login, signup, and password-reset routes.
 * Renders an optional Page Designer background image behind auth content.
 */
export function AuthPageShell({ page, children, className }: AuthPageShellProps): ReactElement {
    return (
        <div data-section="auth" className={cn('relative min-h-dvh bg-background', className)}>
            {page ? (
                <Region
                    page={page}
                    regionId={AUTH_LOGIN_BACKGROUND_REGION_ID}
                    errorElement={null}
                    fallbackElement={null}
                    className={BACKGROUND_REGION_CLASS}
                    aria-hidden
                />
            ) : null}
            <div className="relative z-10">{children}</div>
        </div>
    );
}
